/**
 * Etiquetas visibles del dispositivo Nutri-Matic (español natural).
 * Jerga DeFi estándar (swap, liquidez, pool) en acciones; temática Nutri-Matic en la envoltura del dispositivo.
 * Inglés reservado a marcas: Nutri-Matic, Heart of Gold, Don't Panic, Swap.
 */
export const esNutrimaticLabels = {
  /** Barra de estado — sustantivo + marca, no «marca + sustantivo» al estilo inglés. */
  productName: 'Sintetizador Nutri-Matic',
  edition: 'v42.0 · edición Heart of Gold',
  dontPanic: "DON'T PANIC",
  online: 'En línea',
  selectionBay: 'Bahía de selección',
  reagentCatalog: 'Catálogo de tokens · en vivo',
  /** Panel de precio + gráfica */
  marketQuote: 'Cotización del mercado',
  readoutConsole: 'Consola del sintetizador',
  marketSpotRate: 'Tasa de mercado',
  executionRate: 'Tasa de ejecución',
  /** LCD central multipropósito (desktop) */
  viewportPurpose: 'Modo del display',
  viewportPurposeShort: {
    market: 'Mercado',
    'transaction-confirm': 'Confirmar',
    'transaction-signing': 'Firma',
    'transaction-broadcast': 'Enviando',
    'transaction-success': 'Éxito',
    'transaction-error': 'Error',
  },
  transactionPreviewHint: 'Introduce montos válidos para previsualizar la transacción.',
  quote: 'Cotización',
  marketChart: 'Gráfico del mercado',
  /** Zona de montos e intercambio */
  synthesisCore: 'Panel de swap',
  synthesisYouPay: 'Pagas',
  synthesisYouReceive: 'Recibes',
  changePatch: 'Cambiar par',
  confirmPair: 'Confirmar par',
  backToQuote: 'Cotización',
  inputChamber: 'Token de entrada',
  outputChamber: 'Token de salida',
  dispenseAction: 'Confirmar swap',
  synthesisDeckMode: 'Vista combinada',
  synthesisLayout: 'Disposición del panel',
  synthesisLayoutShort: {
    displays: 'Displays',
    combined: 'Combinado',
  },
  synthesisRate: 'Tasa de cambio',
  hyperspaceReady: 'Listo para operar',
  activePair: 'Par activo',
  selectReagentsHint: 'Elige los tokens en la bahía de selección',
  pairsTab: (count: number) => `Pares (${count})`,
  tokensTab: 'Tokens',
  pairLoaded: 'Seleccionado',
  /** Bahía de selección — instrucciones */
  pairsPanelHint: 'Pulsa un par para usarlo en el swap',
  tokensPanelHint: 'Elige cada token por separado',
  searchPair: 'Buscar par…',
  searchPairCompact: 'Buscar par…',
  searchToken: 'Buscar token o dirección…',
  searchTokenCompact: 'Buscar token…',
  stepSelectPay: 'Paso 1/2 — elige el token que pagas',
  stepSelectReceive: 'Paso 2/2 — elige el token que recibes',
  stepPayLocked: (symbol: string) => `(pagas con ${symbol})`,
  pairReadyHint: 'Par listo — pulsa cualquier token para empezar de nuevo',
  frequentTokens: 'Usados a menudo',
  allKnownTokens: 'Todos los tokens conocidos',
  chooseTokensManually: 'Elegir tokens manualmente →',
  controlPanel: 'Panel de control',
  physicalControls: 'Controles',
  /** Lectura de saldo en display LCD */
  availableBalance: 'Disponible',
  marketsBay: 'Pares',
  toggleChart: 'Gráfico',
  exitDevice: 'Salir',
  settingsBay: 'Ajustes del swap',
  transactionBay: 'Confirmación de swap',
  transactionReview: 'Revisar swap',
  transactionSigning: 'Esperando firma en wallet',
  transactionBroadcast: 'Enviando transacción',
  transactionComplete: 'Swap completado',
  transactionError: 'Error en la transacción',
  priceUpdated: 'Precio actualizado',
  acceptPrice: 'Aceptar precio',
  confirmDispense: 'Confirmar swap',
  confirmAnyway: 'Confirmar de todos modos',
  cancel: 'Cancelar',
  backToSynthesis: 'Volver al swap',
  /** Flujo móvil simplificado */
  mobileSimple: {
    productBrand: 'Nutri-Matic',
    swapTitle: 'Swap',
    reviewScreenTitle: 'Revisión',
    convertTitle: 'Swap',
    reviewTitle: 'Resumen del swap',
    reviewUnavailable:
      'No hay una cotización válida para revisar. Ajusta el monto e inténtalo de nuevo.',
    reviewSwap: 'Revisar swap',
    from: 'De',
    to: 'A',
    continue: 'Continuar',
    convert: 'Swap',
    connectWallet: 'Conectar wallet',
    pickPayAsset: '¿Qué token quieres intercambiar?',
    pickReceiveAsset: '¿Qué token quieres recibir?',
    searchAssets: 'Busca activos',
    pickerSearchPlaceholder: 'Busca por símbolo o dirección de contrato',
    pickerFeatured: 'Destacadas',
    pickerAll: 'Todas',
    pickerWithBalance: 'Con saldo',
    pickerWithoutBalance: 'Sin saldo',
    pickerResults: 'Resultados',
    pickerImportAndSelect: 'Importar y seleccionar',
    pickerImportedBadge: 'Importado',
    pickerRemoveImported: 'Quitar de mi lista',
    pickerSearchingContract: 'Buscando contrato…',
    pickerAddressNotFound: 'No hay token en esta dirección.',
    pickerContractFoundTitle: 'Contrato encontrado',
    pickerContractFoundLead:
      'Este token existe on-chain, pero no forma parte del catálogo oficial de Don\'t Panic.',
    pickerContractFoundHint:
      'Impórtalo para añadirlo a tu lista personal y operar con él en swap, liquidez y otras acciones.',
    pickerContractImport: 'Importar token',
    pickerCancelSearch: 'Cancelar',
    pickerClearSearch: 'Limpiar búsqueda',
    clearAmounts: 'Limpiar montos',
    safeMaxPreset: 'Máx. seguro',
    pickerContractAddressLabel: 'Contrato',
    pickerContractDecimalsLabel: 'Decimales',
    pickerContractCatalogLabel: 'Catálogo',
    pickerNotInCatalog: 'Fuera del catálogo',
    pickerContractDecimals: (decimals: number) => `${decimals} decimales`,
    pickerNoResults: 'No se encontraron activos',
    pickerNoResultsTitle: 'No se encontraron activos',
    pickerNoResultsLead: 'Prueba con otro símbolo, nombre o dirección de contrato.',
    pickerAddressNotFoundTitle: 'Contrato no encontrado',
    pickerConnectWalletHint: 'Conecta tu wallet para ver tus saldos.',
    pickerEmptyWithBalance: 'Ningún activo del catálogo tiene saldo en tu wallet.',
    pickerBalanceAmount: (amount: string, symbol: string) => `${amount} ${symbol}`,
    balance: 'Balance',
    balanceAmount: (amount: string, symbol: string) => `${amount} ${symbol}`,
    approxReceive: (amount: string, symbol: string) => `≈ Recibirás ${amount} ${symbol}`,
    priceImpactLabel: 'Impacto',
    quoteIdle: 'Introduce un monto para ver la cotización.',
    rateIdle: 'Selecciona un par para ver el tipo de cambio.',
    invertRate: 'Invertir par de cotización',
    openSettings: 'Ajustes del swap',
    viewPool: 'Ver pool de liquidez',
    poolOffCatalogHint: 'Pool resuelta on-chain; puede no estar indexada en el mercado.',
    poolNotDeployedHint: 'Esta pool aún no está desplegada on-chain. Puedes crear liquidez para este par.',
    successTitle: (from: string, to: string) => `¡Listo! Intercambiaste ${from} por ${to}`,
    successReceived: (amount: string, symbol: string) =>
      `Recibiste ${amount} ${symbol} en tu wallet`,
    viewExplorer: 'Ver en explorador',
    viewActivity: 'Ver actividad',
    backToConvert: 'Volver al swap',
    backToLiquidityForm: 'Volver al formulario',
    swapOperationCancelled: 'Cancelaste la operación antes de completarla.',
    swapCancelledTitle: 'Operación cancelada',
    swapCancelledKicker: 'Intercambio',
    swapErrorTitle: 'No se pudo completar el swap',
    swapErrorKicker: 'Error en el swap',
    retrySwapAction: 'Reintentar',
    goToToken: (symbol: string) => `Ir a ${symbol}`,
    newSwap: 'Nuevo swap',
    exchangeRate: 'Tipo de cambio',
    minReceived: 'Mínimo recibido',
    maxSold: 'Máximo vendido',
    priceImpact: 'Impacto',
    maxPreset: 'Máx.',
    addLiquidityTitle: 'Añadir liquidez',
    removeLiquidityTitle: 'Retirar liquidez',
    importPoolTitle: 'Importar pool',
    reviewAddLiquidity: 'Revisar depósito',
    reviewRemoveLiquidity: 'Revisar retiro',
    supplyLiquidity: 'Añadir liquidez',
    confirmSupply: 'Confirmar depósito',
    confirmRemove: 'Confirmar retiro',
    createPoolSupply: 'Crear pool y añadir liquidez',
    removeLiquidity: 'Retirar',
    approveToken: (symbol: string) => `Aprobar ${symbol}`,
    approving: 'Aprobando…',
    awaitingWalletSignature: 'Esperando firma en wallet',
    awaitingConfirmation: 'Esperando confirmación',
    firstPoolBanner: 'Eres el primer proveedor de liquidez.',
    firstPoolHint:
      'La proporción de tokens que añadas fijará el precio del pool. Cuando estés conforme, revisa y confirma.',
    liquidityAddBlockedTitle: 'Añadir liquidez no disponible',
    liquidityAddBlockedHint: 'Este par no admite añadir liquidez en este momento.',
    liquidityRemoveBlockedTitle: 'Retiro no disponible',
    liquidityRemoveBlockedHint: 'Este par no admite retirar liquidez en este momento.',
    swapBlockedTitle: "Don't Panic — este par aún no está en la carta",
    swapBlockedHint:
      'El Nutri-Matic no dispensa esta combinación por ahora. No hay nada roto — mayormente inofensivo. Prueba otro par o vuelve más tarde.',
    swapTokenBlockedHint: (symbol: string) =>
      `${symbol} aún no está en la carta de swap de este par. Mayormente inofensivo — elige otro token o vuelve en breve.`,
    poolShare: 'Participación en pool',
    initialPrices: 'Precios iniciales',
    pricesAndShare: 'Precios y participación',
    currentPositionTitle: 'Tu posición actual',
    synthesisSummaryTitle: 'Resumen del depósito',
    withdrawSummaryTitle: 'Resumen del retiro',
    depositAmount: (symbol: string) => `Depósito ${symbol}`,
    withdrawTokenAmount: (symbol: string) => `Recibirás ${symbol}`,
    positionTokenAmount: (symbol: string) => `${symbol} en posición`,
    lpBalance: 'Saldo LP',
    lpToReceive: 'LP a recibir',
    currentPoolShare: 'Participación actual',
    projectedPoolShare: 'Participación proyectada',
    poolRatesTitle: 'Precios del pool',
    poolRateLabel: (base: string, quote: string) => `${base} por ${quote}`,
    liquidityMetricsIdle: 'Introduce montos para ver LP y participación proyectada.',
    liquidityPositionConnectHint: 'Conecta tu wallet para ver tu posición actual.',
    liquidityRemoveMetricsIdle: 'Indica el porcentaje a retirar para ver el desglose.',
    removeAmount: 'Cantidad a retirar',
    youWillReceive: 'Recibirás',
    addSuccessTitle: (a: string, b: string) => `¡Listo! Añadiste liquidez ${a}/${b}`,
    removeSuccessTitle: (a: string, b: string) => `¡Listo! Retiraste liquidez ${a}/${b}`,
    liquidityOperationCancelled: 'Cancelaste la operación antes de completarla.',
    liquidityCancelledTitle: 'Operación cancelada',
    liquidityCancelledAddKicker: 'Depósito de liquidez',
    liquidityCancelledRemoveKicker: 'Retiro de liquidez',
    liquidityErrorTitle: 'No se pudo completar',
    liquidityErrorAddKicker: 'Error al añadir liquidez',
    liquidityErrorRemoveKicker: 'Error al retirar liquidez',
    retryLiquidityAction: 'Reintentar',
    newLiquidityAction: 'Nueva operación',
    importSelectToken: 'Selecciona un token para importar tu pool.',
    importConnectWallet: 'Conecta tu wallet para importar pools.',
    poolFound: 'Pool encontrado',
    importNoPosition: 'Aún no tienes liquidez en este pool.',
    importNoPool: 'No se encontró pool.',
    addLiquidityLink: 'Añadir liquidez',
    createPoolLink: 'Crear pool',
  },
  /** Hub Nutri-Matic de liquidez — readout y placeholders */
  liquidityHub: {
    positionsTitle: 'Posiciones LP',
    readoutKicker: 'Tu posición LP',
    performKicker: 'Inventario',
    onchainFootnote:
      'Uniswap v2 no guarda P&L histórico ni APR on-chain. Aquí ves estado actual: balances LP, participación % y reservas del par vía contrato.',
    placeholderConnectTitle: 'Conecta tu wallet',
    placeholderConnectLead:
      'Sin sesión activa el Nutri-Matic no puede leer tus certificados LP. Conecta tu wallet para continuar.',
    placeholderEmptyTitle: 'El inventario está vacío',
    placeholderEmptyLead:
      'Aún no hay liquidez registrada en tu wallet. Añade liquidez a un par nuevo o importa un pool que ya hayas usado.',
    placeholderSelectTitle: 'Elige una posición',
    placeholderSelectLead:
      'Selecciona un par del inventario para desplegar tu participación, reservas y precio spot en el panel de lectura.',
    placeholderLoadingTitle: 'Sincronizando con la red',
    placeholderLoadingLead: 'Consultando balances LP on-chain. No entres en pánico.',
    actionAdd: 'Añadir liquidez',
    actionImport: 'Importar pool',
    actionAddPair: 'Añadir al par',
    actionRemovePair: 'Retirar del par',
    backToPositions: 'Posiciones LP',
    mobileEmptyConnect: 'Conecta tu wallet para ver posiciones de liquidez.',
    mobileEmptyPositions: 'No tienes posiciones LP visibles.',
    mobileLoading: 'Cargando posiciones…',
  },
} as const
